import {ActivityIndicator, Pressable, StyleSheet, Text} from "react-native";
import Page from "@/lib/components/Page";
import NavButton from "@/lib/components/NavButton";
import {useSession} from "@/lib/auth/auth";
import {aggregateActivityKey} from "@/lib/types";
import {fetchBackendNoContent} from "@/lib/backend";
import ErrorPage from "@/lib/components/ErrorPage";
import SpinnerPage from "@/lib/components/SpinnerPage";
import {useEffect, useState} from "react";
import ActionButton from "@/lib/components/ActionButton";
import ErrorMessage from "@/lib/components/ErrorMessage";
import Split from "@/lib/components/Split";
import Avatar from "@/lib/components/Avatar";
import {useRouter} from "expo-router";
import Message from "@/lib/components/Message";
import {useActivity} from "@/lib/components/ActivityProvider";
import Header from "@/lib/components/Header";
import Nav from "@/lib/components/Nav";
import {Color} from "@/lib/styles";
import Activity from "@/lib/components/Activity";
import {useNotification} from "@/lib/components/NotificationProvider";
import {useAccount} from "@/lib/components/AccountProvider";

export default function Index() {

    const router = useRouter();
    const {session, clearSession} = useSession();
    const {activities, clearActivities, activitiesBusy, activitiesLoading} = useActivity();
    const {acquireNotificationToken} = useNotification();
    const {account, accountError, accountLoading} = useAccount();

    const [loggingOut, setLoggingOut] = useState(false);
    const [logoutError, setLogoutError] = useState<Error | null>(null);

    // Continuously provide backend with fresh notification token.
    // Ignored on web and simulators.
    useEffect(() => {
        acquireNotificationToken();
    }, [session, acquireNotificationToken]);

    if (accountError) {
        return <ErrorPage error={accountError}/>
    } else if (accountLoading || !account) {
        return <SpinnerPage/>
    }

    async function signOut() {
        try {
            setLogoutError(null);
            setLoggingOut(true);
            await fetchBackendNoContent("/account/logout", "POST", session);
            clearSession();
            router.replace(`/u`);
        } catch (err) {
            setLogoutError(err as Error);
        } finally {
            setLoggingOut(false);
        }
    }

    const avatar = account.avatar;

    return (
        <Page scrollable={true}>
            <Pressable style={[styles.card, {backgroundColor: avatar.color}]}
                       onPress={() => {
                           router.navigate(`/u/profile/${avatar.username}`);
                       }}
            >
                <Split
                    left={
                        <>
                            <Avatar avatar={avatar} size={40}/>
                            <Text selectable={false}
                                  style={[styles.username, {color: avatar.contrast_color}]}>{avatar.username}</Text>
                        </>
                    } right={
                    <ActionButton title={"Logg ut"}
                                  onPress={signOut}
                                  color={avatar.contrast_color}
                                  backgroundColor={avatar.color}
                                  icon={"logout"}
                                  busy={loggingOut}
                    />
                }/>
                <NavButton title={"Tilpass brukerprofil"}
                           color={avatar.contrast_color}
                           iconColor={avatar.contrast_color}
                           icon={"edit"}
                           dashed={true}
                           href={`/u/account/edit`}/>
            </Pressable>

            {logoutError && <ErrorMessage error={logoutError}/>}

            {account.pending_emails.map(email => (
                <Message key={email}
                         text={"Vi har sendt en link til " + email + ".\n\nTrykk på linken for å bekrefte e-postadressen."}/>
            ))}

            {account.pending_emails.length === 0 && account.email === null && (
                <Message
                    text={"Du må legge inn e-postadressen din under innstillinger for å få tilgang til alle funksjoner."}/>
            )}

            <NavButton title={"Eventer jeg arrangerer"} icon={"event"} href={`/e/search?filter=owned`}/>
            <NavButton title={"Innstillinger"} icon={"settings"} href={`/u/account/settings`}/>
            <NavButton title={"Hjelp og veiledning"} href={`/u/docs`}/>
            {activities.length === 0 && <NavButton title={"Aktivitetslogg"} href={`/u/account/activity`}/>}

            {activities.length > 0 && (
                <>
                    <Header title={`Aktivitet ( ${activities.length} )`} marginTop={6}/>
                    {activitiesBusy && <ActivityIndicator size={"large"} color={Color.PrimaryText}/>}
                    {activities.map(act => (
                        <Activity key={aggregateActivityKey(act)} activity={act}/>
                    ))}
                    <NavButton title={"Vis full aktivitetslogg"} href={`/u/account/activity`}/>
                    <Nav>
                        <ActionButton title={"Marker alt som lest"}
                                      busy={activitiesLoading}
                                      onPress={() => {
                                          clearActivities();
                                      }}
                        />
                    </Nav>
                </>
            )}

        </Page>
    );
}

const styles = StyleSheet.create({
    card: {
        flexDirection: "column",
        gap: 8,
        borderRadius: 16,
        padding: 6,
    },
    username: {
        fontSize: 16,
        textDecorationLine: "underline",
        textDecorationStyle: "solid",
        fontWeight: 600,
    },
});